import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge,  
  ProgressBar,
  Alert,
  Modal,
  Button
} from 'react-bootstrap';
import { 
  Brain, 
  Cpu, 
  Cloud, 
  Shield, 
  Link, 
  Activity, 
  Glasses, 
  Leaf, 
  Bot, 
  Zap,
  ChevronRight,
  Star,
  TrendingUp,
  Globe,
  Database,
  Smartphone,
  X
} from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

const TechServicesShowcase = () => {
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const services = [
    {
      id: 1,
      title: "AI & ML Solutions",
      subtitle: "LLMs, AGI, AI Agents",
      description: "Automates intelligence with cutting-edge machine learning models and AI agents for enterprise automation.",
      importance: "Huge demand across all sectors for intelligent automation",
      icon: Brain,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderColor: "#667eea",
      status: "Core Focus",
      statusVariant: "primary",
      features: ["Natural Language Processing", "Computer Vision", "Predictive Analytics", "AI Automation"],
      progress: 95,
      detailedInfo: "Our AI & ML Solutions leverage state-of-the-art algorithms to create intelligent systems that can learn, adapt, and make decisions. We specialize in developing custom machine learning models, implementing large language models (LLMs), and creating autonomous AI agents that can handle complex business processes."
    },
    {
      id: 2,
      title: "Quantum Computing",
      subtitle: "Next-Gen Processing",
      description: "Revolutionary computing power for solving complex problems in defense, energy, and finance sectors.",
      importance: "Solves previously unsolvable computational problems",
      icon: Cpu,
      gradient: "linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)",
      borderColor: "#00c6ff",
      status: "Future Tech",
      statusVariant: "info",
      features: ["Quantum Algorithms", "Cryptography", "Optimization", "Research & Development"],
      progress: 60,
      detailedInfo: "Quantum Computing represents the next frontier in computational power. Our research focuses on developing quantum algorithms that can solve optimization problems exponentially faster than classical computers, with applications in cryptography, drug discovery, and financial modeling."
    },
    {
      id: 3,
      title: "Edge & Cloud Computing",
      subtitle: "Distributed Intelligence",
      description: "Low-latency smart services with global reach through advanced cloud and edge computing solutions.",
      importance: "Enables real-time processing and global scalability",
      icon: Cloud,
      gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
      borderColor: "#11998e",
      status: "Active",
      statusVariant: "success",
      features: ["Edge AI", "Serverless Architecture", "Multi-Cloud", "Global CDN"],
      progress: 90,
      detailedInfo: "Our Edge & Cloud Computing solutions bring computation closer to data sources, reducing latency and improving performance. We design distributed architectures that can scale globally while maintaining high availability and security standards."
    },
    {
      id: 4,
      title: "AI-Powered Cybersecurity",
      subtitle: "Intelligent Protection",
      description: "Advanced security solutions powered by AI to protect digital assets in an increasingly connected world.",
      importance: "Critical as digital transformation accelerates globally",
      icon: Shield,
      gradient: "linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)",
      borderColor: "#ff416c",
      status: "High Priority",
      statusVariant: "danger",
      features: ["Threat Detection", "Zero Trust Security", "AI Monitoring", "Incident Response"],
      progress: 88,
      detailedInfo: "AI-Powered Cybersecurity uses machine learning to detect and respond to threats in real-time. Our solutions can identify patterns that traditional security systems miss, providing proactive protection against sophisticated cyber attacks."
    },
    {
      id: 5,
      title: "Blockchain & Web3",
      subtitle: "Decentralized Future",
      description: "Building transparent, decentralized applications and next-generation financial infrastructure.",
      importance: "Powers transparency and decentralized innovation",
      icon: Link,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      borderColor: "#f093fb",
      status: "Emerging",
      statusVariant: "warning",
      features: ["Smart Contracts", "DeFi Solutions", "NFT Platforms", "Crypto Integration"],
      progress: 75,
      detailedInfo: "Blockchain & Web3 technologies enable decentralized applications that operate without central authorities. We develop smart contracts, DeFi protocols, and NFT marketplaces that provide transparency, security, and user ownership of digital assets."
    },
    {
      id: 6,
      title: "Digital Twins & IoT",
      subtitle: "Smart Connected Systems",
      description: "Creating digital replicas for smart cities, industrial automation, and real-time intelligence.",
      importance: "Enables predictive maintenance and smart infrastructure",
      icon: Activity,
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      borderColor: "#4facfe",
      status: "Growing",
      statusVariant: "info",
      features: ["Industrial IoT", "Smart Cities", "Predictive Maintenance", "Real-time Analytics"],
      progress: 82,
      detailedInfo: "Digital Twins & IoT create virtual representations of physical systems, enabling real-time monitoring, simulation, and optimization. Our solutions help organizations predict failures, optimize performance, and make data-driven decisions."
    },
    {
      id: 7,
      title: "AR/VR/XR Solutions",
      subtitle: "Spatial Computing",
      description: "Immersive experiences transforming work, education, and virtual interactions through spatial computing.",
      importance: "Revolutionizing human-computer interaction paradigms",
      icon: Glasses,
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      borderColor: "#fa709a",
      status: "Innovation",
      statusVariant: "secondary",
      features: ["Virtual Training", "Augmented Workflows", "Immersive Experiences", "3D Interfaces"],
      progress: 70,
      detailedInfo: "AR/VR/XR Solutions create immersive digital experiences that blend physical and virtual worlds. We develop applications for training, education, entertainment, and business collaboration that transform how people interact with information."
    },
    {
      id: 8,
      title: "Sustainability Tech",
      subtitle: "Green AI & Climate Tech",
      description: "Climate-focused innovations including green AI solutions and environmental technology platforms.",
      importance: "Addresses global climate challenges with technology",
      icon: Leaf,
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      borderColor: "#a8edea",
      status: "Mission Critical",
      statusVariant: "success",
      features: ["Carbon Tracking", "Energy Optimization", "Climate Modeling", "Sustainable AI"],
      progress: 85,
      detailedInfo: "Sustainability Tech focuses on developing solutions that address climate change and environmental challenges. We create systems for carbon tracking, energy optimization, and sustainable business practices powered by AI and data analytics."
    },
    {
      id: 9,
      title: "Robotics & Autonomous Systems",
      subtitle: "Intelligent Automation",
      description: "Advanced robotics for manufacturing, healthcare, agriculture, and defense applications.",
      importance: "Transforming industries through intelligent automation",
      icon: Bot,
      gradient: "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
      borderColor: "#d299c2",
      status: "Expanding",
      statusVariant: "secondary",
      features: ["Industrial Robots", "Autonomous Vehicles", "Medical Robotics", "Agricultural Automation"],
      progress: 78,
      detailedInfo: "Robotics & Autonomous Systems combine AI, sensors, and mechanical engineering to create intelligent machines that can operate independently. Our solutions enhance productivity, safety, and precision across various industries."
    },
    {
      id: 10,
      title: "NeuroTech & BCI",
      subtitle: "Brain-Computer Interface",
      description: "Pioneering the frontier of human-AI interaction through advanced brain-computer interfaces.",
      importance: "Next frontier in human-technology integration",
      icon: Zap,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderColor: "#667eea",
      status: "R&D",
      statusVariant: "dark",
      features: ["Neural Interfaces", "Cognitive Enhancement", "Medical Applications", "AI-Brain Integration"],
      progress: 45,
      detailedInfo: "NeuroTech & BCI represents the cutting edge of human-computer interaction, developing interfaces that allow direct communication between the brain and digital devices. Our research focuses on medical applications and cognitive enhancement technologies."
    }
  ];

  const stats = [
    { icon: Database, label: "Technologies", value: "10+", variant: "primary" },
    { icon: TrendingUp, label: "Growth Rate", value: "300%", variant: "success" },
    { icon: Globe, label: "Global Reach", value: "50+ Countries", variant: "info" },
    { icon: Smartphone, label: "Projects", value: "200+", variant: "warning" }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards(prev => new Set([...prev, parseInt(entry.target.dataset.cardId)]));
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('[data-card-id]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const handleCardClick = (service) => {
    setSelectedService(service);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedService(null);
  };

  const cardStyles = {
    background: 'rgb(255, 255, 255)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '15px',
    transition: 'all 0.3s ease',
    height: '100%'
  };

  const containerStyles = {
    background: 'linear-gradient(135deg,rgb(252, 252, 252) 0%,rgb(255, 255, 255) 50%,rgb(255, 255, 255) 100%)',
    minHeight: '100vh',
    padding: '3rem 0'
  };

  const headerStyles = {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(15px)',
    borderRadius: '20px',
    padding: '2rem',
    marginBottom: '0.1rem',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  };

  return (
    <div style={containerStyles}>
      <Container fluid className="px-4 ">
        {/* Header Section */}
        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <div style={headerStyles} className="text-center text-dark">
              <div className="d-flex align-items-center justify-content-center mb-4">
                <h1 className="display-3 fw-bold text-primary mb-0">
                  Technology Services
                </h1>
              </div>
              <p className="lead text-dark mb-4">
                Pioneering the future with cutting-edge technologies that transform businesses and shape tomorrow's digital landscape
              </p>
              <Row className="justify-content-center">
                <Col md={4} className="mb-2">
                  <div className="d-flex align-items-center justify-content-center text-dark">
                    <Star size={30} className="me-2 text-warning" />
                    <span>10+ Core Technologies</span>
                  </div>
                </Col>
                <Col md={4} className="mb-2">
                  <div className="d-flex align-items-center justify-content-center text-dark">
                    <TrendingUp size={30} className="me-2 text-success" />
                    <span>Future-Ready Solutions</span>
                  </div>
                </Col>
                <Col md={4} className="mb-2">
                  <div className="d-flex align-items-center justify-content-center text-dark">
                    <Globe size={30} className="me-2 text-primary" />
                    <span>Global Impact</span>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Services Grid */}
        <Row className="g-4 mb-5">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isVisible = visibleCards.has(service.id);
            
            return (
              <Col key={service.id} xl={3} lg={4} md={6} sm={12}>
                <div
                  data-card-id={service.id}
                  className={`h-100 transition-all ${
                    isVisible ? 'animate__animated animate__fadeInUp' : 'opacity-0'
                  }`}
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.6s ease'
                  }}
                >
                  <Card 
                    style={{
                      ...cardStyles,
                      borderLeft: `4px solid ${service.borderColor}`,
                      cursor: 'pointer'
                    }}
                    className="h-100 shadow-lg"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
                    }}
                    onClick={() => handleCardClick(service)}
                  >
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div 
                          style={{
                            background: service.gradient,
                            padding: '12px',
                            borderRadius: '12px',
                            display: 'inline-block',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                          }}
                        >
                          <IconComponent size={24} className="text-dark" />
                        </div>
                        <Badge bg={service.statusVariant} className="fs-6">
                          {service.status}
                        </Badge>
                      </div>

                      <Card.Title className="text-dark fw-bold mb-2">
                        {service.title}
                      </Card.Title>
                      <Card.Subtitle className="text-primary mb-3">
                        {service.subtitle}
                      </Card.Subtitle>
                      <Card.Text className="text-dark flex-grow-1">
                        {service.description}
                      </Card.Text>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-dark">Development Progress</small>
                          <small className="text-dark">{service.progress}%</small>
                        </div>
                        <ProgressBar 
                          now={service.progress} 
                          variant={service.statusVariant}
                          style={{ height: '6px' }}
                        />
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <TrendingUp size={16} className="text-primary me-1" />
                          <small className="text-primary">High Demand</small>
                        </div>
                        <ChevronRight size={20} className="text-primary" />
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            );
          })}
        </Row>

        {/* Stats Section */}
        <Row className="g-4 mb-5">
          {stats.map((stat, index) => (
            <Col key={index} lg={3} md={6} sm={6}>
              <Card style={cardStyles} className="text-center shadow">
                <Card.Body>
                  <stat.icon size={32} className={`text-${stat.variant} mb-3 mx-auto d-block`} />
                  <Card.Title className="text-dark display-6 fw-bold mb-1">
                    {stat.value}
                  </Card.Title>
                  <Card.Text className="text-dark">
                    {stat.label}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Call to Action */}
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card 
              style={{
                ...cardStyles,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                border: '1px solid rgba(255,255,255,0.3)'
              }} 
              className="text-center shadow-lg"
            >
            
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal for Service Details */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        size="lg" 
        centered
        backdrop="static"
      >
        {selectedService && (
          <>
            <Modal.Header 
              style={{
                background: selectedService.gradient,
                border: 'none',
                borderRadius: '15px 15px 0 0'
              }}
              className="text-white"
            >
              <div className="d-flex align-items-center w-100">
                <div 
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    padding: '10px',
                    borderRadius: '10px',
                    marginRight: '15px'
                  }}
                >
                  <selectedService.icon size={28} className="text-white" />
                </div>
                <div className="flex-grow-1">
                  <Modal.Title className="mb-0 text-white fw-bold">
                    {selectedService.title}
                  </Modal.Title>
                  <small className="text-white opacity-75">
                    {selectedService.subtitle}
                  </small>
                </div>
                <Badge 
                  bg="light" 
                  text="dark" 
                  className="fs-6 me-3"
                >
                  {selectedService.status}
                </Badge>
                <Button 
                  variant="link" 
                  onClick={handleCloseModal}
                  className="text-white p-0"
                  style={{ fontSize: '1.5rem', textDecoration: 'none' }}
                >
                  <X size={24} />
                </Button>
              </div>
            </Modal.Header>
            
            <Modal.Body className="p-4">
              <div className="mb-4">
                <h6 className="text-primary fw-bold mb-2">Overview</h6>
                <p className="text-dark mb-3">{selectedService.description}</p>
                <p className="text-dark">{selectedService.detailedInfo}</p>
              </div>

              <div className="mb-4">
                <Alert 
                  variant="info" 
                  className="border-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(13, 202, 240, 0.1), rgba(13, 202, 240, 0.05))',
                    borderLeft: `4px solid ${selectedService.borderColor}`
                  }}
                >
                  <div className="d-flex align-items-start">
                    <TrendingUp size={20} className="text-info me-2 mt-1" />
                    <div>
                      <strong className="text-info">Why it matters:</strong>
                      <div className="text-dark mt-1">{selectedService.importance}</div>
                    </div>
                  </div>
                </Alert>
              </div>

              <div className="mb-4">
                <h6 className="text-primary fw-bold mb-3">Development Progress</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-dark">Current Status</span>
                  <span className="fw-bold" style={{ color: selectedService.borderColor }}>
                    {selectedService.progress}%
                  </span>
                </div>
                <ProgressBar 
                  now={selectedService.progress} 
                  variant={selectedService.statusVariant}
                  style={{ height: '10px', borderRadius: '5px' }}
                />
              </div>

              <div>
                <h6 className="text-primary fw-bold mb-3">Key Features & Capabilities</h6>
                <Row>
                  {selectedService.features.map((feature, idx) => (
                    <Col key={idx} sm={6} className="mb-2">
                      <div className="d-flex align-items-center">
                        <span 
                          style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: selectedService.borderColor,
                            borderRadius: '50%',
                            marginRight: '12px'
                          }}
                        />
                        <span className="text-dark">{feature}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Modal.Body>
            
            <Modal.Footer 
              className="border-0 pt-0"
              style={{ borderRadius: '0 0 15px 15px' }}
            >
              <div className="d-flex justify-content-between w-100 align-items-center">
                <div className="d-flex align-items-center">
                  <TrendingUp size={16} className="text-success me-1" />
                  <small className="text-success fw-bold">High Market Demand</small>
                </div>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCloseModal}
                  className="px-4"
                >
                  Close
                </Button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default TechServicesShowcase;
